import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Slider } from './ui/slider';
import { Shield, AlertTriangle, ChevronRight, CheckCircle2, XCircle, HelpCircle, Briefcase, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Absicherungs-Check (§34d-konform)
 * Berechnet die Absicherungslücke basierend auf:
 * - Beschäftigungsart (Angestellt/Selbstständig)
 * - Nettoeinkommen
 * - Fixkosten
 * - Bestehende Absicherung (BU, Risikoleben, Krankentagegeld)
 * 
 * KEINE Kapitalmarkt-, Rendite- oder Immobilien-Prognosen!
 */

const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Durchschnittliche volle Erwerbsminderungsrente (Stand 2024/2025)
const DURCHSCHNITT_EM_RENTE = 1050;

// LocalStorage key
const STORAGE_KEY = 'absicherungscheck_data';

// Load saved data from localStorage
const loadSavedData = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error loading saved data:', e);
  }
  return null;
};

export const AbsicherungsCheck = () => {
  const navigate = useNavigate();
  
  // Load saved data on initial render
  const savedData = loadSavedData();
  
  // Beschäftigungsart
  const [beschaeftigung, setBeschaeftigung] = useState(savedData?.beschaeftigung || 'angestellt');
  
  // Neue Frage: Abhängige Personen (für Risikoleben)
  const [hatAbhaengige, setHatAbhaengige] = useState(savedData?.hatAbhaengige ?? null); // null = nicht beantwortet, true/false
  
  // Eingaben
  const [nettoEinkommen, setNettoEinkommen] = useState(savedData?.nettoEinkommen || 4500);
  const [fixkosten, setFixkosten] = useState(savedData?.fixkosten || 2500);
  
  // Bestehende Absicherung
  const [buRente, setBuRente] = useState(savedData?.buRente || 0);
  const [krankentagegeld, setKrankentagegeld] = useState(savedData?.krankentagegeld || 0);

  // Save to localStorage whenever data changes
  useEffect(() => {
    const dataToSave = {
      beschaeftigung,
      hatAbhaengige,
      nettoEinkommen,
      fixkosten,
      buRente,
      krankentagegeld
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
  }, [beschaeftigung, hatAbhaengige, nettoEinkommen, fixkosten, buRente, krankentagegeld]);

  // Berechnungen
  const results = useMemo(() => {
    
    // === BU-BERECHNUNG (NEU: bedarfsorientiert) ===
    // VORSCHLAG: Fixkosten + 15-20% Puffer (Nico bestätigt finalen Wert)
    const BU_PUFFER_PROZENT = 0.15; // VORSCHLAG: 15% Puffer
    const empfohleneBU = Math.round(fixkosten * (1 + BU_PUFFER_PROZENT));
    
    // Hinweis: Versicherer begrenzen auf ca. 70-80% Netto (Überversicherungsverbot)
    const maxVersicherbareBU = Math.round(nettoEinkommen * 0.75);
    const empfohleneBUBegrenzt = Math.min(empfohleneBU, maxVersicherbareBU);
    
    // BU-Lücke: Empfohlen minus bestehende BU-Rente
    const buLuecke = Math.max(0, empfohleneBUBegrenzt - buRente);
    
    // === KRANKENTAGEGELD-BERECHNUNG (unverändert, nur Darstellung anders) ===
    let gesetzlichesKTG = 0;
    let ktgLuecke = 0;
    
    if (beschaeftigung === 'angestellt') {
      // Angestellte: GKV-Krankengeld max ~116€/Tag = ~3.500€/Monat (nach 6 Wochen Lohnfortzahlung)
      gesetzlichesKTG = Math.min(nettoEinkommen, 3500);
      ktgLuecke = Math.max(0, nettoEinkommen - gesetzlichesKTG - krankentagegeld);
    } else {
      // Selbstständige: Kein automatisches Krankengeld ohne Wahltarif
      gesetzlichesKTG = 0;
      ktgLuecke = Math.max(0, nettoEinkommen - krankentagegeld);
    }
    
    // === RISIKOLEBEN-BERECHNUNG (NEU: keine konkreten Beträge mehr) ===
    let rlStatus = 'nicht_relevant';
    
    if (hatAbhaengige === true) {
      // Bei "Ja": Relevant, aber keine konkrete Euro-Empfehlung
      rlStatus = 'relevant';
    } else if (hatAbhaengige === false) {
      // Keine abhängigen Personen = nicht relevant
      rlStatus = 'nicht_relevant';
    } else {
      // Noch nicht beantwortet
      rlStatus = 'unbekannt';
    }
    
    // Gesamtlücke monatlich (BU + KTG, NICHT Risikoleben da Einmalzahlung)
    const monatlicheLuecke = buLuecke + ktgLuecke;
    
    // Prozentuale Berechnung für die 3-Stufen-Logik
    const lueckeProzentRaw = empfohleneBUBegrenzt > 0 ? Math.round((monatlicheLuecke / empfohleneBUBegrenzt) * 100) : 0;
    const lueckeProzent = Math.min(100, lueckeProzentRaw);
    const keineAbsicherung = lueckeProzentRaw >= 100;
    
    // Deckungsprozent bezogen auf empfohlene Absicherung
    const deckungProzent = Math.max(0, 100 - lueckeProzent);
    
    // Status-Bewertung
    const buStatus = buRente >= empfohleneBUBegrenzt * 0.9 ? 'gut' : buRente >= empfohleneBUBegrenzt * 0.5 ? 'luecke' : 'kritisch';
    // KTG: neutraler Status "pruefung" statt "kritisch"
    const ktgStatus = ktgLuecke <= 500 ? 'gut' : ktgLuecke <= 1500 ? 'luecke' : 'pruefung';
    
    // Stufe für Ergebnis-Anzeige
    const stufe = lueckeProzentRaw > 50 ? 1 : lueckeProzentRaw >= 15 ? 2 : 3;
    
    return {
      empfohleneBU: empfohleneBUBegrenzt,
      empfohleneBUVorPuffer: empfohleneBU,
      maxVersicherbareBU,
      buLuecke,
      gesetzlichesKTG,
      ktgLuecke,
      monatlicheLuecke,
      buStatus,
      ktgStatus,
      rlStatus,
      fixkostenAnteil: Math.round((fixkosten / nettoEinkommen) * 100),
      deckungProzent,
      lueckeProzent,
      keineAbsicherung,
      stufe,
    };
  }, [nettoEinkommen, fixkosten, buRente, krankentagegeld, beschaeftigung, hatAbhaengige]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'gut': return 'text-emerald';
      case 'luecke': return 'text-orange';
      case 'kritisch': 
      case 'offen': return 'text-red-500';
      case 'pruefung': return 'text-blue-500';
      case 'relevant': return 'text-cobalt';
      case 'nicht_relevant':
      case 'unbekannt': return 'text-slate';
      default: return 'text-slate';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'gut': return <CheckCircle2 className="w-4 h-4 text-emerald" />;
      case 'luecke': return <AlertTriangle className="w-4 h-4 text-orange" />;
      case 'kritisch':
      case 'offen': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pruefung': return <HelpCircle className="w-4 h-4 text-blue-500" />;
      case 'relevant': return <AlertTriangle className="w-4 h-4 text-cobalt" />;
      case 'nicht_relevant': return <CheckCircle2 className="w-4 h-4 text-slate" />;
      case 'unbekannt': return <HelpCircle className="w-4 h-4 text-slate" />;
      default: return null;
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'gut': return 'Ausreichend';
      case 'luecke': return 'Lücke';
      case 'kritisch': return 'Kritisch';
      case 'offen': return 'Nicht vorhanden';
      case 'pruefung': return 'Prüfung im Gespräch';
      case 'relevant': return 'Relevant';
      case 'nicht_relevant': return 'Nicht relevant';
      case 'unbekannt': return 'Bitte Frage beantworten';
      default: return '';
    }
  };

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-ice relative" data-testid="absicherungs-check">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cobalt/[0.02] to-transparent pointer-events-none" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 relative">
        <div className="text-center mb-8 md:mb-12">
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate mb-3 md:mb-4">Absicherungs-Check</div>
          <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-navy tracking-tight mb-3 md:mb-4 px-2">
            Wie groß ist deine Absicherungslücke?
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-charcoal max-w-2xl mx-auto px-2">
            Finde heraus, ob dein Einkommen im Ernstfall abgesichert ist. Und wo die größten Lücken liegen.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr,400px] gap-6 md:gap-8 lg:items-start">
          {/* Eingaben - scrollbar */}
          <div className="bg-white rounded-2xl p-5 sm:p-6 md:p-8 border border-black/5 shadow-lg">
            <h3 className="font-outfit text-lg sm:text-xl font-bold text-navy mb-4 sm:mb-6">Deine Situation</h3>
            
            {/* Beschäftigungsart Toggle */}
            <div className="mb-5 sm:mb-6">
              <label className="text-xs sm:text-sm font-medium text-charcoal block mb-3">Ich bin:</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setBeschaeftigung('angestellt')}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                    beschaeftigung === 'angestellt'
                      ? 'bg-cobalt/10 border-cobalt text-navy font-semibold'
                      : 'bg-white border-slate-200 text-charcoal hover:border-slate-300'
                  }`}
                  data-testid="toggle-angestellt"
                >
                  <Building2 className={`w-4 h-4 ${beschaeftigung === 'angestellt' ? 'text-cobalt' : 'text-slate'}`} />
                  <span className="text-sm">Angestellt</span>
                </button>
                <button
                  onClick={() => setBeschaeftigung('selbststaendig')}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                    beschaeftigung === 'selbststaendig'
                      ? 'bg-cobalt/10 border-cobalt text-navy font-semibold'
                      : 'bg-white border-slate-200 text-charcoal hover:border-slate-300'
                  }`}
                  data-testid="toggle-selbststaendig"
                >
                  <Briefcase className={`w-4 h-4 ${beschaeftigung === 'selbststaendig' ? 'text-cobalt' : 'text-slate'}`} />
                  <span className="text-sm">Selbstständig</span>
                </button>
              </div>
              
              {/* Hinweistext je nach Beschäftigungsart */}
              <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                <p className="text-[10px] sm:text-xs text-slate leading-relaxed">
                  {beschaeftigung === 'angestellt' ? (
                    <>Als Angestellter zahlt dein Arbeitgeber die ersten 6 Wochen dein volles Gehalt weiter. Die Lücke entsteht erst danach, wenn die Krankenkasse nur noch Krankengeld zahlt.</>
                  ) : (
                    <>Als Selbstständiger gibt es keine automatische Lohnfortzahlung. Ohne privates Krankentagegeld entsteht die Lücke ab dem ersten Tag der Arbeitsunfähigkeit.</>
                  )}
                </p>
              </div>
            </div>

            {/* Nettoeinkommen */}
            <div className="mb-5 sm:mb-6">
              <div className="flex justify-between items-baseline mb-2 sm:mb-3">
                <label className="text-xs sm:text-sm font-medium text-charcoal">Nettoeinkommen</label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={nettoEinkommen}
                    onChange={(e) => setNettoEinkommen(Math.min(Math.max(parseInt(e.target.value) || 2000, 2000), 15000))}
                    className="font-mono text-lg sm:text-xl font-bold text-navy text-right bg-transparent border-b border-navy/20 focus:border-cobalt focus:outline-none w-20 sm:w-24"
                    min={2000}
                    max={15000}
                    step={100}
                    data-testid="input-netto"
                  />
                  <span className="font-mono text-lg sm:text-xl font-bold text-navy whitespace-nowrap">€/Monat</span>
                </div>
              </div>
              <Slider
                value={[nettoEinkommen]}
                onValueChange={(value) => setNettoEinkommen(value[0])}
                min={2000}
                max={15000}
                step={100}
                className="w-full"
                data-testid="slider-netto"
              />
              <div className="flex justify-between text-xs text-slate mt-2">
                <span>2.000 €</span>
                <span>15.000 €</span>
              </div>
            </div>

            {/* Fixkosten */}
            <div className="mb-5 sm:mb-6">
              <div className="flex justify-between items-baseline mb-2 sm:mb-3">
                <label className="text-xs sm:text-sm font-medium text-charcoal">Monatliche Fixkosten</label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={fixkosten}
                    onChange={(e) => setFixkosten(Math.min(Math.max(parseInt(e.target.value) || 1000, 1000), 10000))}
                    className="font-mono text-lg sm:text-xl font-bold text-navy text-right bg-transparent border-b border-navy/20 focus:border-cobalt focus:outline-none w-20 sm:w-24"
                    min={1000}
                    max={10000}
                    step={100}
                    data-testid="input-fixkosten"
                  />
                  <span className="font-mono text-lg sm:text-xl font-bold text-navy whitespace-nowrap">€/Monat</span>
                </div>
              </div>
              <Slider
                value={[fixkosten]}
                onValueChange={(value) => setFixkosten(value[0])}
                min={1000}
                max={10000}
                step={100}
                className="w-full"
                data-testid="slider-fixkosten"
              />
              <div className="flex justify-between text-xs text-slate mt-2">
                <span>1.000 €</span>
                <span>10.000 €</span>
              </div>
            </div>

            {/* Datenschutz-Hinweis */}
            <div className="flex items-center gap-2 mb-4 p-2 bg-bone/50 rounded-lg">
              <Shield className="w-3.5 h-3.5 text-slate flex-shrink-0" />
              <p className="text-[10px] sm:text-xs text-slate">
                Deine Angaben werden nicht gespeichert und dienen nur der Berechnung auf dieser Seite.
              </p>
            </div>

            {/* Bestehende Absicherung */}
            <div className="pt-4 border-t border-black/5">
              <h4 className="text-xs sm:text-sm font-semibold text-navy mb-4">Bestehende Absicherung</h4>
              
              {/* BU-Rente */}
              <div className="mb-4">
                <div className="flex justify-between items-baseline mb-2">
                  <label className="text-xs text-charcoal">BU-Rente (falls vorhanden)</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={buRente}
                      onChange={(e) => setBuRente(Math.min(Math.max(parseInt(e.target.value) || 0, 0), 8000))}
                      className="font-mono text-sm font-medium text-navy text-right bg-transparent border-b border-navy/20 focus:border-cobalt focus:outline-none w-16"
                      min={0}
                      max={8000}
                      data-testid="input-bu-rente"
                    />
                    <span className="text-xs text-slate whitespace-nowrap">€/Monat</span>
                  </div>
                </div>
                <Slider
                  value={[buRente]}
                  onValueChange={(value) => setBuRente(value[0])}
                  min={0}
                  max={8000}
                  step={100}
                  className="w-full"
                  data-testid="slider-bu-rente"
                />
              </div>

              {/* Krankentagegeld */}
              <div className="mb-4">
                <div className="flex justify-between items-baseline mb-2">
                  <label className="text-xs text-charcoal">Krankentagegeld (privat)</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={krankentagegeld}
                      onChange={(e) => setKrankentagegeld(Math.min(Math.max(parseInt(e.target.value) || 0, 0), 5000))}
                      className="font-mono text-sm font-medium text-navy text-right bg-transparent border-b border-navy/20 focus:border-cobalt focus:outline-none w-16"
                      min={0}
                      max={5000}
                      data-testid="input-ktg"
                    />
                    <span className="text-xs text-slate whitespace-nowrap">€/Monat</span>
                  </div>
                </div>
                <Slider
                  value={[krankentagegeld]}
                  onValueChange={(value) => setKrankentagegeld(value[0])}
                  min={0}
                  max={5000}
                  step={50}
                  className="w-full"
                  data-testid="slider-ktg"
                />
              </div>

              {/* Neue Frage: Abhängige Personen */}
              <div className="p-3 bg-ice rounded-lg border border-black/5">
                <label className="text-xs sm:text-sm font-medium text-charcoal block mb-2">
                  Sind Personen finanziell von dir abhängig?
                </label>
                <p className="text-[10px] text-slate mb-3">
                  Partner, Kinder, gemeinsamer Kredit o.ä.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setHatAbhaengige(true)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm transition-all ${
                      hatAbhaengige === true
                        ? 'bg-navy text-white font-medium'
                        : 'bg-white border border-black/10 text-charcoal hover:bg-bone'
                    }`}
                    data-testid="abhaengige-ja"
                  >
                    Ja
                  </button>
                  <button
                    onClick={() => setHatAbhaengige(false)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm transition-all ${
                      hatAbhaengige === false
                        ? 'bg-navy text-white font-medium'
                        : 'bg-white border border-black/10 text-charcoal hover:bg-bone'
                    }`}
                    data-testid="abhaengige-nein"
                  >
                    Nein
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Ergebnis - sticky auf Desktop */}
          <div className="lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-120px)] lg:overflow-y-auto space-y-3 sm:space-y-4">
            {/* Hauptergebnis: 3-Stufen-Anzeige */}
            <motion.div 
              className={`rounded-xl p-4 border-2 ${
                results.stufe === 1 
                  ? 'bg-amber-50 border-amber-300' 
                  : results.stufe === 2 
                    ? 'bg-slate-50 border-slate-200' 
                    : 'bg-emerald-50/50 border-emerald-200'
              }`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Lücke als Hauptzahl */}
              <div className="flex items-baseline gap-2 mb-3">
                <span className={`font-mono text-3xl sm:text-4xl font-bold ${
                  results.stufe === 1 ? 'text-amber-700' : 
                  results.stufe === 2 ? 'text-slate-700' : 'text-emerald-700'
                }`}>
                  {formatCurrency(results.monatlicheLuecke)}
                </span>
                <span className={`text-sm ${
                  results.stufe === 1 ? 'text-amber-600' : 
                  results.stufe === 2 ? 'text-slate-500' : 'text-emerald-600'
                }`}>
                  / Monat
                </span>
              </div>
              
              {/* Stufen-spezifischer Text */}
              <p className={`text-sm sm:text-base leading-relaxed ${
                results.stufe === 1 ? 'text-amber-800' : 
                results.stufe === 2 ? 'text-slate-700' : 'text-emerald-800'
              }`}>
                {results.stufe === 1 && (
                  <>
                    {results.keineAbsicherung ? (
                      <>Aktuell besteht keine Absicherung. Bei <span className="font-semibold">{formatCurrency(results.monatlicheLuecke)}</span> monatlicher Lücke solltest du das zeitnah angehen.</>
                    ) : (
                      <>Bei <span className="font-semibold">{formatCurrency(results.monatlicheLuecke)}</span> fehlen dir <span className="font-semibold">{results.lueckeProzent} %</span> deiner empfohlenen Absicherung, falls du berufsunfähig wirst. Das würde ich zeitnah schließen.</>
                    )}
                  </>
                )}
                {results.stufe === 2 && (
                  <>Es fehlen <span className="font-semibold">{formatCurrency(results.monatlicheLuecke)}</span> ({results.lueckeProzent} % deiner empfohlenen Absicherung) im Ernstfall. Das lässt sich meist mit kleinen Anpassungen lösen.</>
                )}
                {results.stufe === 3 && (
                  <>Deine Absicherung deckt <span className="font-semibold">{results.deckungProzent} %</span> der empfohlenen Summe ab. Prüfe alle 2 bis 3 Jahre, ob sich etwas geändert hat (Gehalt, Familie, neuer Vertrag).</>
                )}
              </p>
              
              {/* CTA je nach Stufe */}
              <div className="mt-4">
                {results.stufe === 1 && (
                  <button
                    onClick={() => navigate('/strategiegespraech')}
                    className="w-full group flex items-center justify-center gap-2 px-5 py-3 bg-navy text-white rounded-lg font-medium text-sm hover:bg-charcoal transition-all duration-300"
                    data-testid="absicherung-cta-kritisch"
                  >
                    Erstgespräch vereinbaren
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                )}
                {results.stufe === 2 && (
                  <button
                    onClick={() => navigate('/strategiegespraech')}
                    className="w-full group flex items-center justify-center gap-2 px-5 py-3 bg-navy text-white rounded-lg font-medium text-sm hover:bg-charcoal transition-all duration-300"
                    data-testid="absicherung-cta-optimierung"
                  >
                    Erstgespräch vereinbaren
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                )}
                {results.stufe === 3 && (
                  <button
                    onClick={() => navigate('/strategiegespraech')}
                    className="w-full text-center text-sm text-navy hover:text-charcoal transition-colors py-2"
                    data-testid="absicherung-cta-gut"
                  >
                    Trotzdem mal durchsprechen? →
                  </button>
                )}
              </div>
            </motion.div>

            {/* Detailübersicht */}
            <motion.div 
              className="bg-white rounded-xl p-4 sm:p-6 border border-black/5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h4 className="text-sm font-semibold text-navy mb-4">Detailübersicht</h4>
              
              {/* BU */}
              <div className="flex items-center justify-between py-3 border-b border-black/5">
                <div className="flex items-center gap-2">
                  {getStatusIcon(results.buStatus)}
                  <span className="text-sm text-charcoal">Berufsunfähigkeit</span>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${getStatusColor(results.buStatus)}`}>
                    {getStatusText(results.buStatus)}
                  </div>
                  {results.buLuecke > 0 && (
                    <div className="text-xs text-slate">
                      Lücke: {formatCurrency(results.buLuecke)}/Monat
                    </div>
                  )}
                </div>
              </div>

              {/* Krankentagegeld */}
              <div className="flex items-center justify-between py-3 border-b border-black/5">
                <div className="flex items-center gap-2">
                  {getStatusIcon(results.ktgStatus)}
                  <div>
                    <span className="text-sm text-charcoal">Krankentagegeld</span>
                    {beschaeftigung === 'selbststaendig' && (
                      <span className="block text-[10px] text-slate">Ab Tag 1 relevant</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${getStatusColor(results.ktgStatus)}`}>
                    {results.ktgStatus === 'pruefung' ? 'Mögliche Lücke' : getStatusText(results.ktgStatus)}
                  </div>
                  {results.ktgLuecke > 0 && results.ktgStatus !== 'pruefung' && (
                    <div className="text-xs text-slate">
                      Lücke: {formatCurrency(results.ktgLuecke)}/Monat
                    </div>
                  )}
                  {results.ktgStatus === 'pruefung' && (
                    <div className="text-xs text-slate">
                      Wird im Gespräch geprüft
                    </div>
                  )}
                </div>
              </div>

              {/* Risikoleben */}
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-2">
                  {getStatusIcon(results.rlStatus)}
                  <div>
                    <span className="text-sm text-charcoal">Risikolebensversicherung</span>
                  </div>
                </div>
                <div className="text-right">
                  {hatAbhaengige === false ? (
                    <>
                      <div className="text-sm font-medium text-slate">Nicht relevant</div>
                      <div className="text-xs text-slate max-w-[180px]">
                        Aktuell niemand finanziell abhängig
                      </div>
                    </>
                  ) : hatAbhaengige === null ? (
                    <>
                      <div className="text-sm font-medium text-slate">Bitte beantworten</div>
                      <div className="text-xs text-slate">
                        Frage oben ausfüllen
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={`text-sm font-medium ${getStatusColor(results.rlStatus)}`}>
                        Für dich relevant
                      </div>
                      <div className="text-xs text-slate max-w-[180px]">
                        Höhe im Gespräch ermitteln
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-6 sm:mt-8 flex items-start gap-2 text-[10px] sm:text-xs text-slate max-w-3xl mx-auto px-2">
          <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 mt-0.5" />
          <p>
            Diese Berechnung dient als erste Orientierung. Die tatsächliche Absicherungslücke hängt von deiner 
            individuellen Situation ab (Beruf, Gesundheit, Familienstand). Eine persönliche Beratung ist empfehlenswert.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AbsicherungsCheck;
